import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { api, apiError } from "@/lib/api";
import {
  Users, UserPlus, Search, RefreshCw, Loader2, Check, X as XIcon, AlertTriangle,
  ShieldCheck, ShieldOff, ShieldAlert, KeyRound, Trash2, UserCog, Clock, ChevronDown,
  Lock, Unlock,
} from "lucide-react";

// Lockout state + admin unlock live on the LOCAL portal backend.
const PORTAL_BACKEND = process.env.REACT_APP_BACKEND_URL;
const portalApi = axios.create({ baseURL: PORTAL_BACKEND });

/**
 * AdminUsers — the "People" tab of the Owner Command Center.
 *
 * Backend contract (production Pit Link):
 *   GET   /api/admin/users
 *   GET   /api/admin/users/pending
 *   PATCH /api/admin/users/{id}/approve
 *   PATCH /api/admin/users/{id}/role         body { role }
 *   PATCH /api/admin/users/{id}/suspend
 *   PATCH /api/admin/users/{id}/reactivate
 *   PATCH /api/admin/users/{id}/reset-pin    body { new_pin }
 *   DELETE /api/admin/users/{id}
 *
 * Registration is via POST /api/auth/register (handled by RegisterUser page).
 */
const ROLES = [
  "technician",
  "tradesman",
  "operator",
  "supervisor",
  "planner",
  "superintendent",
  "admin",
  "control",
  "owner",
];

const TABS = [
  { id: "active", label: "Active", icon: ShieldCheck },
  { id: "pending", label: "Pending approval", icon: Clock },
  { id: "suspended", label: "Suspended", icon: ShieldOff },
];

export default function AdminUsers() {
  const [tab, setTab] = useState("active");
  const [users, setUsers] = useState(null);
  const [pending, setPending] = useState(null);
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [actioningId, setActioningId] = useState(null);
  const [pinModal, setPinModal] = useState(null); // {user}
  // Map of username → { is_locked, failed_attempts, locked_at } populated
  // per-page from the local portal backend. Not part of the prod user
  // record — this is the reference implementation of the shared team's
  // account-lockout policy.
  const [lockouts, setLockouts] = useState({});

  const loadLockouts = async (usernames) => {
    const uniq = Array.from(new Set(usernames.filter(Boolean)));
    if (uniq.length === 0) return;
    const results = await Promise.allSettled(
      uniq.map((u) => portalApi.get(`/api/auth/lockout-status/${encodeURIComponent(u)}`).then((r) => r.data)),
    );
    const next = {};
    results.forEach((r, i) => {
      if (r.status === "fulfilled" && r.value && r.value.is_locked) {
        next[uniq[i]] = r.value;
      }
    });
    setLockouts(next);
  };

  const reload = async () => {
    setBusy(true); setErr("");
    try {
      const [uRes, pRes] = await Promise.allSettled([
        api.get("/admin/users"),
        api.get("/admin/users/pending"),
      ]);
      const list = (r) => {
        if (r.status !== "fulfilled") return [];
        const d = r.value.data;
        return Array.isArray(d) ? d : d?.items || d?.results || d?.data || [];
      };
      const activeList = list(uRes);
      const pendingList = list(pRes);
      setUsers(activeList);
      setPending(pendingList);
      // Hydrate lockout state for everyone we're about to render.
      loadLockouts([...activeList, ...pendingList].map((u) => u.username || u.login).filter(Boolean));
    } catch (ex) {
      setErr(apiError(ex));
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const dataForTab = useMemo(() => {
    const isSuspended = (u) => String(u.status || "").toLowerCase().includes("suspend") || u.suspended === true;
    if (tab === "pending") return pending || [];
    const rest = (users || []).filter((u) => !((pending || []).some((p) => (p.id || p._id) === (u.id || u._id))));
    if (tab === "suspended") return rest.filter(isSuspended);
    return rest.filter((u) => !isSuspended(u));
  }, [tab, users, pending]);

  const filtered = useMemo(() => {
    if (!q) return dataForTab;
    const s = q.toLowerCase();
    return dataForTab.filter((u) => {
      const hay = `${u.name || ""} ${u.full_name || ""} ${u.username || ""} ${u.role || ""} ${u.site_code || ""} ${u.email || ""}`.toLowerCase();
      return hay.includes(s);
    });
  }, [dataForTab, q]);

  const withBusy = async (id, fn) => {
    setActioningId(id);
    try { await fn(); await reload(); }
    catch (ex) { alert(apiError(ex)); }
    finally { setActioningId(null); }
  };

  const approve = (u) => withBusy(u.id || u._id, () => api.patch(`/admin/users/${u.id || u._id}/approve`));
  const suspend = (u) => withBusy(u.id || u._id, () => api.patch(`/admin/users/${u.id || u._id}/suspend`));
  const reactivate = (u) => withBusy(u.id || u._id, () => api.patch(`/admin/users/${u.id || u._id}/reactivate`));
  const remove = (u) => {
    if (!window.confirm(`Delete ${u.name || u.username}? This cannot be undone.`)) return;
    return withBusy(u.id || u._id, () => api.delete(`/admin/users/${u.id || u._id}`));
  };
  const changeRole = (u, role) =>
    withBusy(u.id || u._id, () => api.patch(`/admin/users/${u.id || u._id}/role`, { role }));

  const unlock = (u) => {
    const uname = u.username || u.login;
    if (!uname) return;
    return withBusy(u.id || u._id, async () => {
      await portalApi.post("/api/admin/users/unlock", { username: uname }, {
        headers: { "X-Portal-Actor": "portal" },
      });
      // Refresh only the affected row so the LOCKED pill disappears.
      setLockouts((prev) => {
        const next = { ...prev };
        delete next[uname];
        return next;
      });
    });
  };

  const counts = {
    active: (users || []).filter((u) => !((pending || []).some((p) => (p.id || p._id) === (u.id || u._id))) && !String(u.status || "").toLowerCase().includes("suspend")).length,
    pending: (pending || []).length,
    suspended: (users || []).filter((u) => String(u.status || "").toLowerCase().includes("suspend")).length,
  };

  return (
    <div className="space-y-5" data-testid="admin-users-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] font-black text-muted">Owner · Team</div>
          <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight leading-none mt-1">People Directory</h2>
          <div className="text-muted text-sm font-mono-tight mt-1">
            {counts.active} active · {counts.pending} pending · {counts.suspended} suspended
          </div>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-sm btn-ghost" onClick={reload} disabled={busy} data-testid="users-refresh">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
          </button>
          <a className="btn btn-sm btn-primary" href="/register" data-testid="users-add">
            <UserPlus className="w-4 h-4" /> Register user
          </a>
        </div>
      </div>

      {/* Tab strip */}
      <div className="flex flex-wrap gap-2" data-testid="users-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const on = t.id === tab;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-black uppercase tracking-wider transition-colors ${
                on ? "text-black" : "text-white/70 hover:text-white"
              }`}
              style={on ? { background: "var(--primary)" } : { background: "var(--surface-2)", border: "1px solid var(--border)" }}
              data-testid={`users-tab-${t.id}`}
            >
              <Icon className="w-4 h-4" /> {t.label}
              <span className={`ml-1 tabular-nums text-xs ${on ? "text-black/60" : "text-white/60"}`}>
                {counts[t.id]}
              </span>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="panel-flat p-2.5 flex items-center gap-2">
        <Search className="w-4 h-4 ml-2 text-muted" />
        <input
          className="w-full py-3 outline-none bg-transparent text-inherit"
          placeholder="Search by name, username, site, role, email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="users-search"
        />
      </div>

      {err && (
        <div className="pill pill-red !text-sm !py-2 !px-3 justify-start"><AlertTriangle className="w-3.5 h-3.5" /> {err}</div>
      )}

      {/* Rows */}
      {users === null ? (
        <div className="panel-flat p-10 flex items-center justify-center gap-3 text-muted">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading team…
        </div>
      ) : filtered.length === 0 ? (
        <div className="panel-flat p-10 text-center text-muted" data-testid="users-empty">
          <Users className="w-8 h-8 mx-auto mb-3 opacity-60" />
          {tab === "pending" ? "No pending registrations." : tab === "suspended" ? "No suspended users." : "No users found."}
        </div>
      ) : (
        <div className="grid gap-2" data-testid="users-list">
          {filtered.map((u) => (
            <UserRow
              key={u.id || u._id}
              user={u}
              tab={tab}
              actioningId={actioningId}
              lockout={lockouts[u.username || u.login]}
              onApprove={() => approve(u)}
              onSuspend={() => suspend(u)}
              onReactivate={() => reactivate(u)}
              onDelete={() => remove(u)}
              onResetPin={() => setPinModal({ user: u })}
              onRoleChange={(role) => changeRole(u, role)}
              onUnlock={() => unlock(u)}
            />
          ))}
        </div>
      )}

      {pinModal && (
        <ResetPinModal
          user={pinModal.user}
          onClose={() => setPinModal(null)}
          onDone={() => { setPinModal(null); reload(); }}
        />
      )}
    </div>
  );
}

/* ---------------- Row ---------------- */
function UserRow({ user, tab, actioningId, lockout, onApprove, onSuspend, onReactivate, onDelete, onResetPin, onRoleChange, onUnlock }) {
  const [showRole, setShowRole] = useState(false);
  const uid = user.id || user._id;
  const busy = actioningId === uid;
  const initials = String(user.name || user.full_name || user.username || "?")
    .split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const role = String(user.role || "user").toLowerCase();
  const roleTone =
    ["admin", "control", "owner"].includes(role) ? "primary" :
    ["supervisor", "planner", "superintendent"].includes(role) ? "blue" : "muted";
  const isLocked = Boolean(lockout?.is_locked);

  return (
    <div className="panel p-4 flex items-center gap-3 flex-wrap md:flex-nowrap" data-testid={`user-row-${uid}`}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center font-display font-black text-sm shrink-0"
           style={{ background: "var(--primary)", color: "#0a0a0a" }}>
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-display font-black text-base truncate">
            {user.name || user.full_name || user.username}
          </div>
          <span className={`pill !text-[10px] !py-0.5 !px-2 ${roleTone === "primary" ? "pill-orange" : roleTone === "blue" ? "" : ""}`}>
            <ShieldCheck className="w-3 h-3" /> {role}
          </span>
          {isLocked && (
            <span
              className="pill pill-red !text-[10px] !py-0.5 !px-2"
              title={lockout?.locked_at ? `Locked ${new Date(lockout.locked_at).toLocaleString()} after ${lockout.failed_attempts} failed attempts` : "Account is locked"}
              data-testid={`user-locked-${uid}`}
            >
              <Lock className="w-3 h-3" /> Locked
            </span>
          )}
          {user.site_code && (
            <span className="pill !text-[10px] !py-0.5 !px-2">{user.site_code}</span>
          )}
          {user.email && (
            <span className="text-[11px] text-muted font-mono-tight truncate max-w-[220px]">{user.email}</span>
          )}
        </div>
        <div className="text-[11px] text-muted font-mono-tight mt-0.5 truncate">
          @{user.username || user.login || String(uid).slice(0, 6)} · Joined {fmt(user.created_at)}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap justify-end">
        {tab === "pending" && (
          <button className="btn btn-sm btn-primary" onClick={onApprove} disabled={busy} data-testid={`user-approve-${uid}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Approve
          </button>
        )}

        {tab !== "pending" && (
          <div className="relative">
            <button className="btn btn-sm btn-ghost" onClick={() => setShowRole((v) => !v)} disabled={busy} data-testid={`user-role-btn-${uid}`}>
              <UserCog className="w-4 h-4" /> Role <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {showRole && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowRole(false)} />
                <div className="absolute right-0 mt-1 z-50 panel-heavy p-1.5 w-48" data-testid={`user-role-menu-${uid}`}>
                  {ROLES.map((r) => (
                    <button
                      key={r}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm font-mono-tight hover:bg-white/5 ${r === role ? "text-orange-400" : ""}`}
                      onClick={() => { setShowRole(false); onRoleChange(r); }}
                      data-testid={`user-role-set-${uid}-${r}`}
                    >
                      {r === role ? "✓ " : ""}{r}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {tab === "active" && (
          <button className="btn btn-sm btn-ghost" onClick={onSuspend} disabled={busy} title="Suspend" data-testid={`user-suspend-${uid}`}>
            <ShieldOff className="w-4 h-4" /> Suspend
          </button>
        )}
        {tab === "suspended" && (
          <button className="btn btn-sm" onClick={onReactivate} disabled={busy} title="Reactivate" data-testid={`user-reactivate-${uid}`}>
            <ShieldAlert className="w-4 h-4" /> Reactivate
          </button>
        )}
        {tab !== "pending" && isLocked && (
          <button
            className="btn btn-sm"
            onClick={onUnlock}
            disabled={busy}
            title={`Unlock ${user.username || user.login} (5 failed PIN attempts)`}
            data-testid={`user-unlock-${uid}`}
            style={{ background: "rgba(74,222,128,0.14)", border: "1px solid rgba(74,222,128,0.4)", color: "#4ade80" }}
          >
            <Unlock className="w-4 h-4" /> Unlock
          </button>
        )}
        {tab !== "pending" && (
          <button className="btn btn-sm btn-ghost" onClick={onResetPin} disabled={busy} title="Reset PIN" data-testid={`user-reset-pin-${uid}`}>
            <KeyRound className="w-4 h-4" />
          </button>
        )}
        <button className="btn btn-sm btn-ghost text-red-400 hover:text-red-300" onClick={onDelete} disabled={busy} title="Delete" data-testid={`user-delete-${uid}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

/* ---------------- Reset-PIN modal ---------------- */
function ResetPinModal({ user, onClose, onDone }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const uid = user.id || user._id;

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{4}$/.test(pin)) { setMsg({ type: "err", text: "PIN must be 4 digits." }); return; }
    setBusy(true); setMsg(null);
    try {
      await api.patch(`/admin/users/${uid}/reset-pin`, { new_pin: pin });
      // Also clear the local lockout counter + write an audit event.
      const uname = user.username || user.login;
      if (uname) {
        portalApi.post("/api/admin/users/reset-pin", { username: uname, new_pin: pin }, {
          headers: { "X-Portal-Actor": "portal" },
        }).catch(() => {});
      }
      setMsg({ type: "ok", text: "PIN reset." });
      setTimeout(onDone, 500);
    } catch (ex) {
      setBusy(false);
      setMsg({ type: "err", text: apiError(ex) });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="reset-pin-modal">
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }} onClick={onClose} />
      <form onSubmit={submit} className="relative panel-heavy w-full max-w-md p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
               style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
            <KeyRound className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-muted">Reset PIN</div>
            <div className="font-display font-black text-lg tracking-tight">
              {user.name || user.username}
            </div>
          </div>
          <button type="button" className="btn btn-sm btn-ghost !px-2" onClick={onClose}><XIcon className="w-4 h-4" /></button>
        </div>
        <div className="field">
          <label>New 4-digit PIN</label>
          <input
            className="input font-mono-tight text-2xl tracking-widest text-center"
            type="password"
            inputMode="numeric"
            maxLength={4}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
            placeholder="••••"
            data-testid="reset-pin-input"
            autoFocus
          />
        </div>
        {msg && (
          <div className={`pill ${msg.type === "ok" ? "pill-green" : "pill-red"} !text-xs !py-2 !px-3 justify-start mt-3`}>
            {msg.type === "ok" ? <Check className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />} {msg.text}
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={busy || !pin} data-testid="reset-pin-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Reset PIN
          </button>
        </div>
      </form>
    </div>
  );
}

function fmt(v) { if (!v) return "—"; try { return new Date(v).toLocaleDateString(); } catch { return String(v); } }
